import { z } from "zod";

import { ERROR_MSG } from "@/constants/ErrorMessage.constant";
import { ONE, TEN_THOUSAND, THOUSAND } from "@/constants/constants";
import { EmploymentType, TAny } from "@/interfaces/globals";
import {
  AddressValidatorRegex,
  abnValidator,
  alphanumericValidator,
  basicValidator,
  emailValidator,
  imageValidator,
  phoneNumberValidator,
} from "@/lib/validators";

export const AdminUser = z
  .object({
    id: z.string().optional(),
    userId: z.string().optional(),
    name: z
      .object({
        first: basicValidator(true, "First Name is required"),
        last: basicValidator(true, "Last Name is required"),
      })
      .superRefine((data, ctx) => {
        if (!data.first?.length && !data.last?.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
          });
        }
        if (data?.first?.length && !data?.last?.length) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Last Name is required",
          });
        }
      }),
    email: z
      .string({
        required_error: "Admin user email is required.",
      })
      .trim()
      .email({ message: ERROR_MSG.INVALID_EMAIL })
      .optional(),
    position: basicValidator(
      true,
      "Admin user position is required.",
      1,
      100,
      true,
    ),
    permissions: z.object({
      emailNotification: z.boolean(),
      customerPortal: z.boolean(),
      projectFinance: z.boolean(),
    }),
  })
  .superRefine((data, ctx) => {
    if ((data.email || data.name) && !data.position) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Admin user position is required.",
      });
    }
  });

export const SubContractor = z.object({
  name: alphanumericValidator({
    allowHyphen: true,
    required: true,
    allowSpace: true,
    minLength: 2,
    maxLength: 30,
    message: ERROR_MSG.REQUIRED,
  }),

  address: z
    .object({
      latitude: z.string(),
      longitude: z.string(),
      label: z.string().optional(),
    })
    .optional(),
  phoneNumber: phoneNumberValidator(true, ERROR_MSG.INVALID_PHONE),
  email: emailValidator(true, ERROR_MSG.INVALID_EMAIL),
  accountEmail: emailValidator(true, ERROR_MSG.INVALID_EMAIL),
  abn: abnValidator(true),
  image: imageValidator(2, false, ERROR_MSG.INVALID_IMAGE),
});

export type TSubContractorSchema = z.infer<typeof SubContractor>;

export const WorkspaceSchema = z.object({
  name: basicValidator(true, "Workspace Name  is required", 2, THOUSAND),
  subDomain: alphanumericValidator({
    allowHyphen: true,
    required: true,
    allowSpace: false,
    minLength: 2,
    message: "Subdomain is required.",
    maxLength: 12,
  }).superRefine((data, ctx) => {
    if (data?.toLowerCase()?.trim() === "admin") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `subdomain cannot be named 'admin'`,
      });
    } else if (data?.toLowerCase()?.trim() === "customer") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `subdomain cannot be named 'customer'`,
      });
    }
  }),
  abn: abnValidator(true),
  email: z
    .string({
      required_error: ERROR_MSG.INVALID_EMAIL,
    })
    .trim()
    .min(2, { message: ERROR_MSG.INVALID_EMAIL })
    .max(40)
    .email({ message: ERROR_MSG.INVALID_EMAIL }),
  phoneNumber: phoneNumberValidator(true, ERROR_MSG.INVALID_PHONE),
  state: z
    .string({
      required_error: "State is required.",
    })
    .trim()
    .min(2, {
      message: "State is required.",
    })
    .max(50, {
      message: "State is too long.",
    }),
  image: imageValidator(4, false, ERROR_MSG.INVALID_IMAGE),
  workspaceCode: basicValidator(false, "Required", 0, 3),
  address: z
    .string({
      required_error: "Address is required.",
    })
    .trim()
    .regex(AddressValidatorRegex, {
      message: "Invalid address format",
    })
    .min(2, {
      message: "Address is required.",
    })
    .max(50, {
      message: "Address is too long.",
    }),
});

export type TWorkspaceSchema = z.infer<typeof WorkspaceSchema>;

export const ManagerSchema = z.object({
  firstName: alphanumericValidator({
    allowHyphen: true,
    required: true,
    allowSpace: true,
    minLength: 2,
    maxLength: 20,
    message: ERROR_MSG.REQUIRED,
  }),
  middleName: alphanumericValidator({
    allowHyphen: true,
    required: false,
    allowSpace: true,
    maxLength: 20,
    message: ERROR_MSG.REQUIRED,
  }),
  lastName: alphanumericValidator({
    allowHyphen: true,
    required: true,
    allowSpace: true,
    minLength: 2,
    maxLength: 20,
    message: ERROR_MSG.REQUIRED,
  }),
  email: z.string().trim().email({ message: ERROR_MSG.INVALID_EMAIL }),
  phoneNumber: phoneNumberValidator(true, ERROR_MSG.INVALID_PHONE),
  address: z
    .string({
      required_error: "Address is required.",
    })
    .trim()
    .regex(AddressValidatorRegex, {
      message: "Invalid address format",
    })
    .min(2, {
      message: "Address is required.",
    })
    .max(50, {
      message: "Address is too long.",
    }),
  image: imageValidator(2, false, ERROR_MSG.REQUIRED_IMAGE),
  employmentType: z.enum(Object.values(EmploymentType) as TAny, {
    message: "Employment type is required",
  }),
  workspaceCode: z.string().optional(),
});

export type TManagerSchema = z.infer<typeof ManagerSchema>;

export const PrivacySettingsSchema = z.object({
  id: z.string().optional(),
  heading: basicValidator(),
  description: basicValidator(true, "Required", ONE, TEN_THOUSAND),
});

export type TPrivacySettingsSchema = z.infer<typeof PrivacySettingsSchema>;
